import { Box, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { getRuntimeEnvironmentName } from '@/config/urls';
import { useAppShell } from '@/shell';

function resolveEnvironmentTone(theme: Theme, environmentName: string): string {
  if (environmentName === 'production') {
    return theme.palette.success.main;
  }

  if (environmentName === 'stage') {
    return theme.palette.warning.main;
  }

  if (environmentName === 'test') {
    return theme.palette.info.main;
  }

  return theme.palette.text.secondary;
}

export function EnvironmentBadge() {
  const { t } = useTranslation('entity-portal');
  const { environment } = useAppShell();
  const runtimeEnvironmentName = getRuntimeEnvironmentName();
  const environmentName = environment || runtimeEnvironmentName;

  const environmentLabel =
    environmentName === 'production'
      ? 'PROD'
      : environmentName === 'stage'
        ? 'STAGE'
        : environmentName === 'test'
          ? 'TEST'
          : environmentName === 'local'
            ? 'LOCAL'
            : 'DEV';

  return (
    <Box
      aria-label={t('appHeader.appHeaderEnvironmentLabel', {
        environment: environmentLabel,
      })}
      sx={{
        display: { xs: 'none', sm: 'inline-flex' },
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        bgcolor: (theme) => {
          const tone = resolveEnvironmentTone(theme, environmentName);
          return alpha(tone, theme.palette.mode === 'dark' ? 0.24 : 0.12);
        },
        border: '1px solid',
        borderColor: (theme) => {
          const tone = resolveEnvironmentTone(theme, environmentName);
          return alpha(tone, theme.palette.mode === 'dark' ? 0.52 : 0.28);
        },
        flexShrink: 0,
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: (theme) => resolveEnvironmentTone(theme, environmentName),
          flexShrink: 0,
        }}
      />
      <Typography
        variant="chip"
        sx={{
          color: (theme) => resolveEnvironmentTone(theme, environmentName),
        }}
      >
        {environmentLabel}
      </Typography>
    </Box>
  );
}
